import { useEffect, useState, useCallback, useRef } from "react";

import { supabase } from "@/shared/lib/supabase";
import { TASK_IDS } from "@/shared/constants/taskIds";
import { AUTH_ROUTES, POST_LOSS_ROUTES, ONBOARDING_ROUTES } from "@/shared/constants/paths";
import type { Database } from "@/shared/types/database.types";

type EstateRow = Database["public"]["Tables"]["estates"]["Row"];

async function hasUploadedIdCard(userId: number): Promise<boolean> {
  const { data } = await supabase
    .from("estate_documents")
    .select("id")
    .eq("document_type", "identification_card")
    .eq("uploaded_by_user_id", userId)
    .limit(1);
  return !!data && data.length > 0;
}

async function hasPendingInvitation(email: string, userId: number): Promise<boolean> {
  // Only match if the user has a pending (not yet accepted) invitation
  // AND no user_estates link yet — this targets the race condition where
  // acceptInvitation is still running, without bypassing primary onboarding
  // for users who happen to have old invitations.
  const { data: link } = await supabase
    .from("user_estates")
    .select("estate_id")
    .eq("user_id", userId)
    .limit(1)
    .maybeSingle();

  if (link) return false;

  const { data: invitation } = await supabase
    .from("estate_invitations")
    .select("id")
    .eq("email", email.toLowerCase())
    .neq("status", "accepted")
    .limit(1)
    .maybeSingle();

  return !!invitation;
}

type Destination =
  | { type: "loading" }
  | { type: "login" }
  | { type: "onboarding"; screen: string }
  | { type: "dashboard" };

interface UseAuthRouterReturn {
  destination: Destination;
  isLoading: boolean;
  person: EstateRow | null;
  relationshipToDecedent: string | null;
  isInvitedMember: boolean;
  route: string;
  isAccountComplete: boolean;
  isIdentityComplete: boolean;
  refetch: () => Promise<void>;
}

export function useAuthRouter(): UseAuthRouterReturn {
  const [destination, setDestination] = useState<Destination>({
    type: "loading",
  });
  const [person, setPerson] = useState<EstateRow | null>(null);
  const [relationshipToDecedent, setRelationshipToDecedent] = useState<string | null>(null);
  const [identityComplete, setIdentityComplete] = useState(false);
  const [invitedMember, setInvitedMember] = useState(false);
  const isMountedRef = useRef(true);

  const determineDestination = useCallback(async () => {
    setDestination({ type: "loading" });

    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!isMountedRef.current) return;

    if (!session) {
      setDestination({ type: "login" });
      return;
    }

    const { data: userData } = await supabase
      .from("users")
      .select(
        "user_id, first_name, phone_number, last_active_estate_id, customizations, completed_onboarding",
      )
      .eq("auth_id", session.user.id)
      .single();

    if (!isMountedRef.current) return;

    if (!userData) {
      setDestination({ type: "onboarding", screen: "has-obituary" });
      return;
    }

    // Invited members: acceptInvitation runs asynchronously after the invite
    // flow completes, so completed_onboarding and user_estates may not be set
    // yet. Only triggers when the user has a pending invitation AND no
    // user_estates link — this narrows the check to the actual race condition
    // without bypassing primary onboarding for users with old invitations.
    if (!userData.completed_onboarding && session.user.email) {
      const isInvitedMember = await hasPendingInvitation(session.user.email, userData.user_id);
      if (!isMountedRef.current) return;

      if (isInvitedMember) {
        const hasId = await hasUploadedIdCard(userData.user_id);
        if (!isMountedRef.current) return;
        setInvitedMember(true);
        setDestination(hasId ? { type: "dashboard" } : { type: "onboarding", screen: "upload-id" });
        return;
      }
    }

    if (!userData.completed_onboarding && !userData.first_name) {
      setDestination({ type: "onboarding", screen: "about-yourself" });
      return;
    }

    const { data: userPerson } = await supabase
      .from("user_estates")
      .select("estate_id, relationship_to_decedent")
      .eq("user_id", userData.user_id)
      .limit(1)
      .single();

    if (!isMountedRef.current) return;

    if (!userPerson) {
      setDestination({ type: "onboarding", screen: "has-obituary" });
      return;
    }

    if (userPerson.relationship_to_decedent) {
      setRelationshipToDecedent(userPerson.relationship_to_decedent as string);
    }

    const { data: personData } = await supabase
      .from("estates")
      .select("*")
      .eq("estate_id", userPerson.estate_id)
      .single();

    if (!isMountedRef.current) return;

    if (!personData) {
      setDestination({ type: "onboarding", screen: "has-obituary" });
      return;
    }

    setPerson(personData);

    // Completed onboarding -> check if invited member needs ID, then dashboard
    if (userData.completed_onboarding) {
      const isInvited = personData.owner_user_id !== userData.user_id;

      if (isInvited) {
        const hasId = await hasUploadedIdCard(userData.user_id);
        if (!isMountedRef.current) return;
        setInvitedMember(true);
        if (!hasId) {
          setDestination({ type: "onboarding", screen: "upload-id" });
          return;
        }
      }

      const { data: identityTask } = await supabase
        .from("estate_tasks")
        .select("status")
        .eq("estate_id", userPerson.estate_id)
        .eq("task_id", TASK_IDS.NOTIFY_CREDIT_BUREAUS)
        .maybeSingle();

      if (!isMountedRef.current) return;

      const isComplete =
        identityTask?.status === "completed" ||
        identityTask?.status === "skipped" ||
        identityTask?.status === "in_progress";
      setIdentityComplete(isComplete);
      setDestination({ type: "dashboard" });
      return;
    }

    // Still onboarding -- determine which screen to resume at
    const isObitPath = !!personData.obituary_data;

    if (!personData.full_name) {
      setDestination({ type: "onboarding", screen: "has-obituary" });
      return;
    }

    if (!userPerson.relationship_to_decedent || !personData.location) {
      const screen = isObitPath ? "confirm-details-obit" : "decedent-contact-details-manual";
      setDestination({ type: "onboarding", screen });
      return;
    }

    setDestination({ type: "onboarding", screen: "choose-plan" });
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    determineDestination();

    return () => {
      isMountedRef.current = false;
    };
  }, [determineDestination]);

  const getRoute = (): string => {
    switch (destination.type) {
      case "loading":
        return "";
      case "login":
        return AUTH_ROUTES.SIGN_IN;
      case "dashboard":
        return POST_LOSS_ROUTES.OVERVIEW;
      case "onboarding":
        return ONBOARDING_ROUTES.ACCOUNT;
    }
  };

  const isAccountComplete = destination.type === "dashboard";
  const isIdentityComplete = isAccountComplete && identityComplete;

  return {
    destination,
    isLoading: destination.type === "loading",
    person,
    relationshipToDecedent,
    isInvitedMember: invitedMember,
    route: getRoute(),
    isAccountComplete,
    isIdentityComplete,
    refetch: determineDestination,
  };
}
